import { computed } from 'vue';

import { useMessageContext } from './provider.js';
import { useMapGetter } from 'dashboard/composables/store';
import { ORIENTATION } from './constants';
import {
  getParticipantColor,
  groupParticipantColor,
  groupParticipantName,
  groupRoster,
  isGroupConversation,
} from './groupParticipant.js';
import { isAceleresPersonalConversationForUser } from 'dashboard/helper/aceleresConversationState';
import {
  aiReplyDisplayName,
  conversationAttendantIdentity,
  isUnattributedWameMessage,
} from './messageAuthor.js';

// A DECISAO de confiar ou nao no `sender`, e quem assina quando nao da para
// confiar, mora em messageAuthor.js — sem Vue e sem store, para que o avatar
// (Message.vue) e o player de audio (audioIdentity.js) leiam a MESMA resposta.
// Cada copia dessa regra que existiu divergiu.

/**
 * Na caixa Pessoais a conversa acontece no NÚMERO DO PRÓPRIO VENDEDOR: ele é o
 * dono da conexão, e do outro lado está um contato dele. Carimbar "Angelo:" em
 * cada mensagem que ele mesmo mandou do próprio WhatsApp não identifica nada —
 * só afasta a tela do WhatsApp, que é a referência da caixa.
 *
 * O gate é a CONVERSA, não a aba aberta na URL: a mesma conversa pode ser
 * alcançada por busca, deep link ou reload, e o nome não pode piscar conforme
 * o caminho de entrada. A regra usada aqui é a mesma que decide a lista
 * (`ConversationFinder#personal_scope` no Rails), então a conversa que sai de
 * Pessoais para Meus volta a exibir o nome no mesmo instante em que troca de
 * caixa — nas demais caixas nada muda.
 *
 * Três coisas ficam DE FORA de propósito:
 *   - a mensagem recebida (esquerda), que é o contato — e, em grupo, o
 *     participante: quem fala num grupo aparece no WhatsApp também;
 *   - a nota privada, que é registro interno e precisa do autor;
 *   - o rótulo "IA", que não é vendedor nenhum.
 */
export function useHideSellerName() {
  const { orientation, inboxId, conversationId, isPrivate, contentAttributes } =
    useMessageContext();

  const inboxGetter = useMapGetter('inboxes/getInbox');
  const conversationGetter = useMapGetter('getConversationById');
  const currentUser = useMapGetter('getCurrentUser');

  return computed(() => {
    if (orientation.value !== ORIENTATION.RIGHT) return false;
    if (isPrivate?.value) return false;
    // A new panel message keeps its author visible independently of delivery.
    if (
      typeof contentAttributes?.value?.aceleres_send_signature === 'boolean'
    ) {
      return false;
    }

    const conversation = conversationGetter.value(conversationId.value);
    if (!conversation) return false;

    return isAceleresPersonalConversationForUser({
      conversation,
      inbox: inboxGetter.value(inboxId.value) || {},
      currentUserId: currentUser.value?.id,
    });
  });
}

/**
 * Cor do nome de quem participa de uma conversa de grupo.
 *
 * O JID normalizado e a identidade principal. Nas mensagens de saida criadas
 * pelo Chatwoot, que podem nao carregar JID de participante, o id real do
 * remetente vem antes do nome. Fora de grupos devolve vazio para preservar o
 * visual das conversas privadas.
 *
 * @param {import('vue').ComputedRef<string>} displaySenderName nome resolvido
 * @returns {import('vue').ComputedRef<string>}
 */
export function useGroupParticipantColor(displaySenderName) {
  const { contentAttributes, conversationId, senderId } = useMessageContext();
  const conversationGetter = useMapGetter('getConversationById');

  return computed(() => {
    const conversation = conversationGetter.value(conversationId.value);
    if (!isGroupConversation(conversation)) return '';

    const participantColor = groupParticipantColor(
      contentAttributes.value,
      groupRoster(conversation)
    );
    if (participantColor) return participantColor;

    // Aceleres 2026-09-14: o nome vem ANTES do `senderId` também na saída. A
    // bolha otimista e o eco do envio chegam sem `sender_id`, e a atualização
    // seguinte traz o id: com o id na frente, a chave do hash trocava no meio
    // do envio e o nome do vendedor piscava entre duas cores. O nome é a única
    // chave presente em todos esses estados.
    const participantId = displaySenderName.value || senderId.value;

    return getParticipantColor(participantId);
  });
}

/**
 * Nome do remetente exibido na bolha.
 *
 * Esta regra precisa existir num lugar só. Ela já vivia em Base.vue e em
 * Message.vue (avatarInfo); quando o álbum de stickers nasceu, ele desligou o
 * nome do BaseBubble (`hide-sender-name`) e reimplementou o rótulo lendo
 * `sender.name` cru — sem o guard do WAME. Resultado: um par de figurinhas
 * enviadas do celular aparecia como "Facundo" (nome do inbox) enquanto solto e
 * virava o nome do dono do token admin ao ser agrupado, reescrevendo a autoria
 * exibida e quebrando a auditoria de quem enviou.
 */
export function useDisplaySenderName() {
  const { sender, contentAttributes, orientation, inboxId, conversationId } =
    useMessageContext();

  const inboxGetter = useMapGetter('inboxes/getInbox');
  const conversationGetter = useMapGetter('getConversationById');
  const hideSellerName = useHideSellerName();

  return computed(() => {
    const aiDisplayName = aiReplyDisplayName({
      contentAttributes: contentAttributes.value,
      senderType: sender.value?.type,
      senderName: sender.value?.name,
    });
    // A resposta continua identificada como IA pelo avatar, mas não ganha um
    // prefixo textual acima do corpo da mensagem.
    if (aiDisplayName) return '';

    // Para as demais mensagens, as regras abaixo preservam o autor resolvido
    // pelo sender, pelo responsável da conversa ou pelo fallback apropriado.
    if (hideSellerName.value) return '';

    const senderName = sender.value?.name;
    const senderType = String(sender.value?.type || '').toLowerCase();

    const conversation = conversationGetter.value(conversationId.value);
    if (orientation.value === ORIENTATION.LEFT) {
      // Em grupo, `sender` é o CONTATO DO GRUPO — o mesmo para todo mundo que
      // fala ali. Quem enviou está em content_attributes e precisa aparecer
      // sobre toda mensagem recebida, independentemente do tipo de mídia.
      if (isGroupConversation(conversation)) {
        const participant = groupParticipantName(
          contentAttributes.value,
          groupRoster(conversation)
        );
        return participant || senderName;
      }

      // Numa conversa individual o cabeçalho já identifica o contato. Repetir
      // o nome em cada texto, imagem, GIF, sticker ou documento só adiciona
      // ruído visual; o nome fica reservado às conversas de grupo.
      return '';
    }

    const inbox = inboxGetter.value(inboxId.value) || {};
    const isApiInboxUserSender =
      inbox.channel_type === 'Channel::Api' && senderType === 'user';

    if (isApiInboxUserSender) {
      // Com autor resolvido, quem assina e quem ESCREVEU — e nada aqui pode
      // trocar isso pelo responsavel da conversa, que muda a cada
      // transferencia e reescreveria a autoria de mensagens antigas.
      if (
        !isUnattributedWameMessage({
          contentAttributes: contentAttributes.value,
          senderType,
          inbox,
        })
      ) {
        return senderName;
      }

      // Sem autor identificavel a mensagem nao foi enviada por atendente
      // nenhum do painel: ela saiu do celular (ou de outro sistema) no mesmo
      // numero, e o `sender` gravado e o dono do token admin da empresa.
      //
      // Ate 31/08/2026 este caminho caia no NOME DO INBOX ("Financeiro"),
      // herdado de d4b4208220 (12/06): a INSTANCIA assinando no lugar da
      // atendente, com a conversa alternando "Tahina B." e "Financeiro" balao
      // a balao. Tirar o nome do canal resolveu metade — sobrou a alternancia
      // entre o nome do atendente e um balao sem rotulo nenhum.
      //
      // Desde 04/09/2026 quem assina aqui e o ATENDENTE RESPONSAVEL PELO LEAD:
      // o unico humano que o painel conhece nesta mensagem. O nome do canal
      // nao volta em hipotese alguma; sem responsavel, o balao fica sem
      // rotulo. A assinatura que sai pela WAME segue outra regra
      // (SendWameChatwootMessage::authorLabelToSend, no Laravel): la o rotulo
      // marca TROCA de locutor no WhatsApp do cliente, e mensagem sincronizada
      // do celular nem chega a passar pela ponte de saida.
      return conversationAttendantIdentity(conversation)?.name ?? '';
    }

    if (senderName && senderType === 'user') {
      // Preserve the actual human sender when the message has one.
      return senderName;
    }

    return senderName;
  });
}
